/**
 * Auth context — talks to the existing F1 backend (functions/api/auth/*).
 *
 * Token model (F1 ADR-0006/0008/0009):
 *   - `__Host-tot_refresh` HttpOnly cookie holds the long-lived refresh_token
 *     (Path=/, SameSite=Strict, Secure). The browser NEVER reads it.
 *   - access_token + id_token live in this provider's React state (memory).
 *     On reload they're gone — we recover by calling /api/auth/refresh which
 *     reads the cookie server-side and returns fresh tokens.
 *   - api() reads the id_token via the getter registered by this provider so
 *     every authenticated call carries `Authorization: Bearer <id_token>`.
 *
 * Why id_token (not access_token):
 *   - Cognito custom attrs (custom:tot_tenant_id, custom:tot_org_id) only land
 *     in ID tokens. /api/me needs them, so it validates ID tokens.
 *   - For pure scope-checks you could use access_token; today we don't have
 *     any such endpoints, so id_token everywhere keeps the wiring uniform.
 *
 * Group → persona mapping (from F1 Cognito setup):
 *   - attendee   → consumer / ticket-holder PWA
 *   - partner    → restaurant / hotel / artist / nonprofit backoffice
 *   - organizer  → festival admin
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, setBearerTokenGetter } from "@/lib/api";
import { signIn as signInApi } from "@/lib/authClient";

export type Role = "attendee" | "partner" | "organizer";

export interface AuthUser {
  sub: string;
  email: string;
  name?: string;
  groups: Role[];
  /** Cognito custom:tot_tenant_id — null until the User Pool is configured + the
   *  user has the attribute populated. Snake_case to match the /api/me wire shape. */
  tenant_id: string | null;
  /** Cognito custom:tot_org_id — same caveats. Drives partner-scoped queries. */
  org_id: string | null;
}

interface AuthTokens {
  accessToken: string;
  idToken: string;
  /** Epoch seconds when the tokens expire — used to schedule a refresh. */
  expiresAt: number;
}

interface TokenExchangeResponse {
  access_token: string;
  id_token: string;
  expires_in: number;
  token_type: "Bearer";
}

interface TokenRefreshResponse {
  access_token: string;
  /** Cognito only returns a fresh id_token sometimes — pre-existing one stays valid otherwise. */
  id_token?: string;
  expires_in: number;
  token_type: "Bearer";
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  /** Refetch /api/me (e.g. after a role change). */
  refresh: () => Promise<void>;
  /** Email + password sign-in. Returns tokens on success (also stored via
   *  setTokensFromExchange internally) or throws ApiError on failure. */
  signIn: (input: { email: string; password: string }) => Promise<void>;
  /** Stash tokens received from /api/auth/signin (also called internally). */
  setTokensFromExchange: (resp: TokenExchangeResponse) => Promise<void>;
  /** Server-side logout (clears HttpOnly refresh cookie), then bounce. */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** A bit of headroom to avoid sending a request RIGHT as the token expires. */
const REFRESH_SKEW_SECONDS = 60;

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

// -----------------------------------------------------------------------------
// PKCE helpers (RFC 7636). Kept at module scope so login() and callback share
// the same encoding without the risk of the two files drifting.
// -----------------------------------------------------------------------------

/** base64url — no padding, URL-safe alphabet. */
function base64UrlEncode(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** Generate a cryptographically-random base64url string.
 *  32 bytes → 43 chars, safely inside PKCE's 43..128 range. */
export function randomBase64Url(bytes = 32): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return base64UrlEncode(buf);
}

/** SHA-256(input) → base64url. Used to derive `code_challenge` from
 *  `code_verifier` in the S256 PKCE method. */
export async function sha256Base64Url(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", enc);
  return base64UrlEncode(new Uint8Array(digest));
}

function tokensFromExchange(resp: TokenExchangeResponse | TokenRefreshResponse, prev: AuthTokens | null): AuthTokens | null {
  const accessToken = resp.access_token;
  const idToken = ("id_token" in resp && resp.id_token) ? resp.id_token : prev?.idToken;
  if (!accessToken || !idToken) return null;
  return {
    accessToken,
    idToken,
    expiresAt: nowSeconds() + resp.expires_in,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Latest tokens — captured via ref so the bearer getter ALWAYS reads the
  // freshest value (state captured in a closure would stale out after refresh).
  const tokensRef = useRef<AuthTokens | null>(null);
  tokensRef.current = tokens;

  // Register the Bearer-token getter with the api() wrapper once, on mount.
  useEffect(() => {
    // The project convention is id_token as Bearer (see /api/me and the
    // existing verifyCognitoBearer calls). id_token carries the email +
    // custom Cognito attrs which several endpoints need. All new endpoints
    // (checkout/session, me/pass, calendar auth-optional) verify with
    // tokenUse:'id' to match.
    setBearerTokenGetter(() => tokensRef.current?.idToken ?? null);
    return () => setBearerTokenGetter(() => null);
  }, []);

  /** Call /api/auth/refresh — recovers tokens from the HttpOnly refresh cookie. */
  const tryRefresh = useCallback(async (): Promise<AuthTokens | null> => {
    try {
      // Don't go through api() here — that wrapper would try to add a Bearer
      // we don't have yet. Direct fetch keeps the call clean.
      const r = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) return null;
      const body = (await r.json()) as TokenRefreshResponse;
      const next = tokensFromExchange(body, tokensRef.current);
      if (!next) return null;
      // Sync ref alongside setTokens (which is async). Boot sequence + proactive
      // refresh both call fetchMe() next and need the fresh bearer immediately.
      tokensRef.current = next;
      setTokens(next);
      return next;
    } catch {
      return null;
    }
  }, []);

  /** Pull /api/me with whatever Bearer is currently registered. */
  const fetchMe = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const me = await api<AuthUser>("/api/me");
      return me;
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return null;
      console.warn("[auth] /api/me failed:", e);
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const me = await fetchMe();
      setUser(me);
    } finally {
      setLoading(false);
    }
  }, [fetchMe]);

  // Boot sequence: try to recover tokens via refresh cookie, then /api/me.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const recovered = await tryRefresh();
      if (cancelled) return;
      if (!recovered) {
        setUser(null);
        setLoading(false);
        return;
      }
      const me = await fetchMe();
      if (!cancelled) {
        setUser(me);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tryRefresh, fetchMe]);

  // Proactive refresh — schedule the next /api/auth/refresh just before the
  // current token expires. Avoids 401 spikes mid-session.
  useEffect(() => {
    if (!tokens) return;
    const msUntilRefresh = Math.max(
      5_000,
      (tokens.expiresAt - REFRESH_SKEW_SECONDS - nowSeconds()) * 1000
    );
    const t = setTimeout(() => {
      void tryRefresh();
    }, msUntilRefresh);
    return () => clearTimeout(t);
  }, [tokens, tryRefresh]);

  const setTokensFromExchange = useCallback(
    async (resp: TokenExchangeResponse) => {
      const next = tokensFromExchange(resp, tokensRef.current);
      if (!next) return;
      // Update the ref SYNCHRONOUSLY — setTokens is async (React state), but
      // fetchMe() below reads `tokensRef.current` via the Bearer getter and
      // must see the fresh token IMMEDIATELY. Without this, /api/me is called
      // with the OLD (null) bearer → 401 → user stays null → RequireAuth loop.
      tokensRef.current = next;
      setTokens(next);
      const me = await fetchMe();
      setUser(me);
    },
    [fetchMe]
  );

  /**
   * Email + password sign-in via BFF (/api/auth/signin).
   * The Function computes SECRET_HASH and calls Cognito InitiateAuth
   * with USER_PASSWORD_AUTH. On success, sets __Host-tot_refresh cookie
   * and returns access+id tokens. We hand those to setTokensFromExchange
   * which populates the AuthContext.
   *
   * Throws ApiError from api() on failure — pages should catch and show
   * an inline error. Error codes: auth_bad_credentials, auth_not_confirmed,
   * auth_rate_limited, etc. (see functions/_shared/cognitoApi.ts).
   */
  const signIn = useCallback(
    async (input: { email: string; password: string }) => {
      const tokens = await signInApi(input);
      await setTokensFromExchange(tokens);
    },
    [setTokensFromExchange],
  );

  const logout = useCallback(async () => {
    try {
      await api<void>("/api/auth/logout", { method: "POST" });
    } finally {
      setTokens(null);
      setUser(null);
      window.location.assign("/");
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      refresh,
      setTokensFromExchange,
      signIn,
      logout,
    }),
    [user, loading, refresh, setTokensFromExchange, signIn, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/**
 * Pure predicate — used by route guards (RequireAuth) and conditional UI.
 */
export function hasRole(user: AuthUser | null, role: Role): boolean {
  if (!user) return false;
  return user.groups.includes(role);
}
